import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
import useLocales from "src/hooks/useLocales";
import FilterDialog from "../FilterDialog";
import { IAppliedFilter } from "src/@types/FilterDialogInterfaces";

interface FilterMenuProps {
  inputRef: any;
  handleOpenMenu: any;
  handleCloseMenu: any;
  open: boolean;
  anchorEl: Element | ((element: Element) => Element) | null | undefined;
  filters: any;
  handleOnDelete?: (key: string) => void;
  rows?: number;
  loader?: boolean;
  children: React.ReactNode;
};

const FilterMenu = ({
  inputRef,
  handleOpenMenu,
  handleCloseMenu,
  open,
  filters,
  handleOnDelete,
  loader,
  children,
}: FilterMenuProps) => {
  const { translate } = useLocales();
  const contentRef = useRef<HTMLDivElement>(null);

  const filtersRef = useRef(filters);
  const handleOnDeleteRef = useRef(handleOnDelete);
  useEffect(() => {
    filtersRef.current = filters;
    handleOnDeleteRef.current = handleOnDelete;
  });

  const appliedFilters: Record<string, IAppliedFilter> = Object.fromEntries(
    Object.entries(filters ?? {}).map(([k, item]: any) => [
      k,
      {
        name: item?.name ?? k,
        label: translate(item?.label),
        displayValue: translate(item?.displayValue),
      },
    ]),
  );

  const handleApply = () => {
    const form = contentRef.current?.querySelector("form");
    if (form) {
      if (typeof (form as any).requestSubmit === "function") {
        (form as any).requestSubmit();
      } else {
        form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    } else {
      handleCloseMenu();
    }
  };

  const handleClearAll = handleOnDelete
    ? async () => {
      let keys = Object.keys(filtersRef.current ?? {});
      let guard = keys.length + 5;
      while (keys.length > 0 && guard-- > 0) {
        handleOnDeleteRef.current?.(keys[0]);
        await new Promise((resolve) => setTimeout(resolve, 0));
        keys = Object.keys(filtersRef.current ?? {});
      }
      handleCloseMenu();
    }
    : undefined;

  return (
    <Box ref={inputRef} sx={{ width: "100%" }}>
      <FilterDialog
        buttonLabel={translate("add_filter")}
        dialogTitle={translate("filter_by")}
        appliedFilters={appliedFilters}
        isLoading={loader}
        onDelete={handleOnDelete}
        onClearAll={handleClearAll}
        onSubmit={handleApply}
        open={open}
        onOpen={handleOpenMenu}
        onClose={handleCloseMenu}
      >
        <Box
          ref={contentRef}
          sx={{
            '& form button[type="submit"]': { display: "none" },
            "& form .MuiGrid-item:has(> .MuiTypography-h6)": {
              display: "none",
            },
          }}
        >
          {children}
        </Box>
      </FilterDialog>
    </Box>
  );
};

export default FilterMenu;
